import { parsePhoneNumber, AsYouType } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'

/**
 * 電話番号ヘルパー関数（フロントエンド用）
 * 非通知番号の判定、表示用フォーマットなど
 */

/**
 * 非通知番号のリスト
 * Twilioが非通知や表示できない番号として使用する特殊な番号
 */
const ANONYMOUS_NUMBERS: string[] = [
  '+266696687',
  '+7378742833',
  '+2562533',
  '+8656696',
  '+86282452253',
  'Anonymous',  // Twilioが非通知の場合に返す文字列
  'anonymous'   // 小文字バージョンも念のため
]

/**
 * 電話番号かどうかを判定（簡易チェック）
 */
const looksLikePhoneNumber = (value: string): boolean => {
  if (!value || typeof value !== 'string') return false
  // +で始まるか、数字を含むかチェック
  return /^[+\d]/.test(value.trim()) && /\d/.test(value)
}

/**
 * 電話番号が非通知かどうかを判定
 */
export function isAnonymousNumber(phoneNumber: string | null | undefined): boolean {
  if (!phoneNumber) return false

  // 正規化して比較（大文字小文字を区別しない）
  const normalized = phoneNumber.trim()
  const normalizedLower = normalized.toLowerCase()

  // 完全一致チェック
  if (ANONYMOUS_NUMBERS.includes(normalized)) return true

  // 大文字小文字を区別しないチェック
  return ANONYMOUS_NUMBERS.some(num => num.toLowerCase() === normalizedLower)
}

/**
 * 電話番号を表示用にフォーマット
 * 非通知の場合は「非通知」と表示
 * libphonenumber-jsを使用して国内形式でフォーマット
 */
export function formatPhoneNumberForDisplay(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) return '-'

  // 非通知番号チェック
  if (isAnonymousNumber(phoneNumber)) {
    return '非通知'
  }

  // 電話番号ではない文字列（client, operatorなど）はそのまま返す
  if (!looksLikePhoneNumber(phoneNumber)) {
    return phoneNumber
  }

  try {
    const parsed = parsePhoneNumber(phoneNumber)
    if (parsed) {
      return parsed.formatNational()
    }
  } catch (error) {
    // パースエラーの場合は元の値を返す
  }

  return phoneNumber
}

/**
 * 電話番号を保存用にフォーマット（E.164形式）
 * libphonenumber-jsを使用して国際形式に変換
 */
export function formatPhoneNumberForSave(phoneNumber: string | null | undefined, defaultCountry: CountryCode = 'JP'): string | null {
  if (!phoneNumber) return null

  // 電話番号ではない文字列の場合はnullを返す
  if (!looksLikePhoneNumber(phoneNumber)) {
    console.warn('Not a valid phone number format:', phoneNumber)
    return null
  }

  try {
    const parsed = parsePhoneNumber(phoneNumber, defaultCountry)
    if (parsed && parsed.isValid()) {
      return parsed.format('E.164')
    }
  } catch (error) {
    console.warn('Failed to parse phone number for save:', phoneNumber, error)
  }

  return null
}

/**
 * 入力中の電話番号をリアルタイムでフォーマット（入力補助用）
 * libphonenumber-jsを使用してフォーマット
 */
export function formatPhoneNumberAsYouType(input: string | null | undefined, defaultCountry: CountryCode = 'JP'): string {
  if (!input) return ''

  // 電話番号ではない文字列の場合はそのまま返す
  if (!looksLikePhoneNumber(input)) {
    return input
  }

  try {
    const formatter = new AsYouType(defaultCountry)
    return formatter.input(input)
  } catch (error) {
    // フォーマットエラーの場合は元の入力を返す
    return input
  }
}

/**
 * 電話番号が有効かどうかを検証
 * libphonenumber-jsを使用して検証
 */
export function isValidPhoneNumber(phoneNumber: string | null | undefined, defaultCountry: CountryCode = 'JP'): boolean {
  if (!phoneNumber) return false

  // 電話番号ではない文字列の場合はfalse
  if (!looksLikePhoneNumber(phoneNumber)) {
    return false
  }

  try {
    const parsed = parsePhoneNumber(phoneNumber, defaultCountry)
    return parsed ? parsed.isValid() : false
  } catch (error) {
    return false
  }
}

/**
 * 非通知番号のリストを取得（テスト用）
 */
export function getAnonymousNumbers(): string[] {
  return [...ANONYMOUS_NUMBERS]
}
